/* ==========================================================================
   Cart discount code — shared apply/remove helpers on window.EssentialsVault.
   Both operations go through the Cart AJAX update.js `discount` field, verified
   against the live store: {discount:"CODE"} records the code, {discount:""}
   clears it, and Shopify carries an applied code into checkout.
   cart-drawer.js and cart-page.js call these, then re-render their own body so
   the applied chip / empty form reflects the new state.
   ========================================================================== */
(function () {
  window.EssentialsVault = window.EssentialsVault || {};

  function root() {
    return (window.Shopify && window.Shopify.routes && window.Shopify.routes.root)
      || (window.theme && window.theme.routes && window.theme.routes.root)
      || '/';
  }

  async function update(discount) {
    const res = await fetch(`${root()}cart/update.js`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify({ discount }),
    });
    if (!res.ok) throw new Error('cart update failed');
    return res.json();
  }

  function paymentReady(ready) {
    window.EssentialsVault.cartDiscount.paymentReady = ready;
    document.dispatchEvent(new Event('ev:payment-discount-ready'));
  }

  // Native product checkout creates its own cart. Shopify's shareable link
  // records the accepted code in the browser as well as the AJAX cart.
  async function share(code) {
    const controller = new AbortController();
    const timeout = setTimeout(() => controller.abort(), 8000);
    try {
      const res = await fetch(`${root()}discount/${encodeURIComponent(code)}?redirect=${encodeURIComponent(`${root()}cart.js`)}`, {
        credentials: 'same-origin', signal: controller.signal,
      });
      if (!res.ok) throw new Error('discount preparation failed');
    } finally { clearTimeout(timeout); }
  }

  function clearBrowserDiscount() {
    // Shopify's shareable link sets this host-only, root-path cookie. Cart AJAX
    // removal alone leaves it behind and would silently reapply the old code.
    document.cookie = 'discount_code=; Max-Age=0; Path=/; SameSite=Lax';
  }

  async function prepare(cart) {
    paymentReady(false);
    const codes = (cart.discount_codes || []).filter(d => d.applicable);
    // A shareable link carries one code. Keep combined discounts on the normal
    // cart checkout path instead of silently dropping part of the combination.
    if (codes.length > 1) return;
    try {
      if (codes.length) await share(codes[0].code);
      paymentReady(true);
    } catch (_) { /* Ordinary cart checkout still retains the accepted code. */ }
  }

  let initialPreparation = Promise.resolve();
  window.EssentialsVault.cartDiscount = {
    paymentReady: false,
    // Apply `code`. Resolves { applicable } — false when the code is unknown,
    // expired, or not valid for this cart's contents. Shopify still records a
    // non-applicable code, so the caller clears it on failure (see remove).
    async apply(code) {
      const c = (code || '').trim();
      if (!c) return { applicable: false };
      await initialPreparation;
      const cart = await update(c);
      const dc = (cart.discount_codes || []).find(
        (d) => (d.code || '').toUpperCase() === c.toUpperCase()
      );
      const applicable = !!(dc && dc.applicable);
      if (applicable) {
        await prepare(cart);
        document.dispatchEvent(new CustomEvent('ev:discount-applied'));
      }
      return { applicable };
    },

    // Clear all discount codes from the cart.
    async remove() {
      await initialPreparation;
      await update('');
      clearBrowserDiscount();
      paymentReady(true);
    },
  };

  // Restore codes already accepted before this page (or this release) loaded.
  // The optional native button waits; add-to-cart never depends on this read.
  if (document.querySelector('[data-native-payment]')) {
    const controller = new AbortController();
    const timeout = setTimeout(() => controller.abort(), 8000);
    initialPreparation = fetch(`${root()}cart.js`, { credentials: 'same-origin', signal: controller.signal })
      .then(res => { if (!res.ok) throw new Error('cart read failed'); return res.json(); })
      .then(prepare).catch(() => paymentReady(false))
      .finally(() => clearTimeout(timeout));
  }
})();
